import { defineStore } from 'pinia';
import { Ref, computed, nextTick, onBeforeMount, ref, shallowRef } from 'vue';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSessionStorageRef } from '@/composables/session-storage';
import type { SavedView, StatePersistor } from '@/modules/saved-views/types';
import { uiCustomizationService } from '@/connect/services/ui-customization';
import { UIConfigVisibility } from '@/connect/gen/consts/uiconfigconsts_pb';
import { cloneDeep } from 'lodash';
import { useSocketStore } from '@/store/socket-store';
import { useRoute, useRouter } from '@/router/router';
import { logger } from '@/modules/common/services/logger.service';

// having a limit is a temporary measure until we have a BE feature capable of migrating
// saved views when filtering structure changes (and potentially breaks existing views)
// until then, there will be cases we'll have to wipe out the saved views in the DB
// enforcing a max will prevent users from investing too much time creating views
const MAX_SAVED_VIEWS = 5;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StatePersistorAny = StatePersistor<any>;

const stores = new Map<string, unknown>();
export const makeUseSavedViews = <T extends StatePersistorAny>(
  name: string,
  statePersistorClass: new () => T
): (() => SavedViewsStore<T>) => {
  let store = stores.get(name) as () => SavedViewsStore<T>;
  if (store === undefined) {
    store = createStore(name, statePersistorClass);
    stores.set(name, store);
  }
  return store;
};

function createStore<T>(name: string, statePersistorClass: new () => StatePersistor<T>) {
  const key = `savedViews-${name}` as const;
  return defineStore(key, () => {
    const router = useRouter();

    const statePersistor = new statePersistorClass();
    const snackbarManager = useSnackbarManager();
    const dialogManager = useDialogManager();

    const savedViews = ref<Ref<Array<SavedView<T>>>>(ref([]));
    const hasSavedViews = computed(() => savedViews.value.length > 0);

    const initialSavedViewId = shallowRef<string | undefined>(undefined);

    function setInitialSavedViewId(savedViewId: string) {
      initialSavedViewId.value = savedViewId;
    }

    const selectedView = shallowRef<SavedView<T> | null>(null);

    const selectedIndex = computed(() => {
      if (selectedView.value === null) return null;
      const index = savedViews.value.findIndex((view) => view.id === selectedView.value?.id);
      return index === -1 ? null : index;
    });

    const editingView = shallowRef<SavedView<T> | null>(null);
    const editingIndex = computed(() => {
      const index = savedViews.value.findIndex((v) => v === editingView.value);
      return index === -1 ? null : index;
    });

    function setEditingView(view: SavedView<T> | null) {
      editingView.value = view;
    }

    const previousSelectedView = shallowRef<SavedView<T> | null>(null);

    function setPreviousSelectedView(view: SavedView<T> | null) {
      previousSelectedView.value = view;
    }

    const isCreating = computed(() => editingView.value && !editingView.value.id);

    const isSaving = shallowRef(false);

    const refreshData = shallowRef<() => Promise<void> | void>(Promise.resolve);

    function registerRefreshData(callback: () => Promise<void> | void) {
      refreshData.value = callback;
    }

    const socketStore = useSocketStore();

    // @TODO: what if user is editing something and gets a socket event?
    // currenyly UI is left in a weird state when it happens
    socketStore.useEventHandler('refreshUIConfigs', async (payload) => {
      // ignore events from other namespaces
      if (payload !== name) return;

      await refreshSavedViews();

      const previouslySelectedView = savedViews.value.find((v) => v.id === selectedView.value?.id);
      if (previouslySelectedView) {
        // re-select the same view so we get the fresh object
        await selectView(previouslySelectedView);
        await nextTick();
        await refreshData.value();
      }
    });

    // ensure savedViewId in the URL is always synced
    // get a fresh route instance in both set and get to avoid stale reactive values
    const urlSavedViewId = computed({
      get: () => {
        // type from the route is incorrect, even though the prop in the path is marked with "?"
        return useRoute().params.savedViewId as string | undefined;
      },
      set: (id: string | undefined) => {
        const route = useRoute();
        if (!route.name) {
          throw new Error('All routes must have "name"');
        }
        const currentSavedViewId = route.params.savedViewId as string | undefined;

        if (id && id !== currentSavedViewId) {
          // add or update savedViewId parameter
          void router.replace({ name: route.name!, params: { savedViewId: id } });
          lastViewedId.value = id;
        } else if (!id && currentSavedViewId) {
          // remove savedViewId from URL when no view is selected or view has no id
          void router.replace({ name: route.name! });
          lastViewedId.value = null;
        }
      },
    });

    onBeforeMount(async () => {
      await refreshSavedViews();

      if (!hasSavedViews.value) {
        // no saved views yet, do nothing
        return;
      }

      const targetViewId = initialSavedViewId.value ?? urlSavedViewId.value ?? lastViewedId.value;

      if (!targetViewId) {
        // no URL parameter, no initial saved view prop and no last viewed: auto select first view
        await selectFirstViewIfPossible();
        return;
      }

      const view = savedViews.value.find((v) => v.id === targetViewId);
      if (view) {
        await selectView(view);
        // Clear the initialSavedViewId after successful selection
        initialSavedViewId.value = undefined;
      } else {
        logger.warn(`Saved view with id "${targetViewId}" not found, defaulting to first view`);
        await selectView(savedViews.value[0] ?? null);
      }
    });

    async function refreshSavedViews() {
      savedViews.value = await uiCustomizationService.getSavedViews<T>(name);
    }

    async function selectView(targetView: SavedView<T> | null) {
      if (selectedView.value === targetView && targetView !== null) {
        // if already on the selected view, do nothing, unless it's a reset call.
        return;
      }

      if (editingView.value) {
        // cancel any ongoing editing first
        // we shouldn't use cancelEdit() here, because it will also reset the selected view
        editingView.value = null;
      }

      selectedView.value = targetView;

      if (selectedView.value === null) {
        statePersistor?.resetState();
        urlSavedViewId.value = undefined;
        return;
      }

      urlSavedViewId.value = selectedView.value.id;

      // apply saved view filter state
      if (selectedView.value.filterState && statePersistor) {
        await statePersistor.setState(selectedView.value.filterState);
      }
    }

    const isStateDirty = computed(() => {
      if (selectedView.value === null) return false;
      return statePersistor.isDirty(selectedView.value.filterState ?? ({} as T));
    });

    async function addNewView() {
      if (enforceMaxViewsLimit()) {
        return;
      }

      previousSelectedView.value = selectedView.value;

      const newView: SavedView<T> = {
        title: '',
        description: '',
        visibility: UIConfigVisibility.PRIVATE,
        filterState: statePersistor.getState(),
        isOwner: true,
      };

      savedViews.value.push(newView);
      await selectView(newView);
      editingView.value = newView;
    }

    function syncView(view: SavedView<T>) {
      dialogManager.ask({
        color: 'primary',
        title: 'Update Saved View State',
        message: `Are you sure you want to update and overwrite "${view.title}" with the current view state?`,
        acceptText: 'Update',
        rejectText: 'Cancel',
        onAccept: async () => {
          if (!view.id) return;
          const updatedView = {
            ...view,
            filterState: statePersistor.getState(),
          };
          const res = await uiCustomizationService.modifySavedView(view.id, updatedView);
          if (!res.success) {
            snackbarManager.error('Failed to sync view.');
            return;
          }
          snackbarManager.confirm('View succesfully updated');
          await refreshSavedViews();

          // Ensure the synced view remains selected after refresh
          const viewIndex = savedViews.value.findIndex((v) => v.id === view.id);
          if (viewIndex !== -1) {
            await selectView(savedViews.value[viewIndex]);
          }
        },
      });
    }

    function deleteView(view: SavedView<T>) {
      if (editingView.value !== null) {
        // cancel any ongoing editing first
        void cancelEdit();
      }

      dialogManager.ask({
        color: 'error',
        title: 'Delete Saved View',
        message: `Are you sure you want to delete "${view.title}"?`,
        acceptText: 'Delete',
        rejectText: 'Cancel',
        onAccept: async () => {
          const res = await uiCustomizationService.deleteSavedView(view.id!);
          if (!res.success) {
            snackbarManager.error('Failed to delete view.');
            return;
          }

          const previoslySelectedId = selectedView.value?.id;
          await refreshSavedViews();

          if (previoslySelectedId && previoslySelectedId !== view.id) {
            await selectByIdIfPossible(previoslySelectedId);
          } else {
            // if no view was selected, or the selected view is the one we deleted, select first view
            await selectFirstViewIfPossible();
          }

          snackbarManager.confirm('View deleted successfully');
        },
      });
    }

    async function duplicateView(view: SavedView<T>) {
      if (enforceMaxViewsLimit()) {
        return;
      }

      const originalView = view;
      const index = savedViews.value.findIndex((v) => v.id === view.id);
      if (index === -1) return;

      previousSelectedView.value = selectedView.value;

      const duplicatedView: SavedView<T> = {
        title: `${originalView.title} (Copy)`,
        description: originalView.description,
        visibility: UIConfigVisibility.PRIVATE,
        filterState: cloneDeep(originalView.filterState),
        isOwner: true,
      };
      savedViews.value.splice(index + 1, 0, duplicatedView);
      await selectView(duplicatedView);
      editingView.value = duplicatedView;
    }

    async function saveChanges(title: string, description: string, visibility: UIConfigVisibility) {
      if (isSaving.value || !editingView.value) return;

      isSaving.value = true;

      try {
        const savedViewId = editingView.value?.id
          ? await modifySavedView(title, description, visibility)
          : await createSavedView(title, description, visibility);

        await refreshSavedViews();
        // another refresh will happen after the socket event arrives
        // resulting in a double fetch; we accept this for now,
        // since it makes the remaining workflow simpler
        await selectByIdIfPossible(savedViewId);
        editingView.value = null;
      } catch (error) {
        snackbarManager.error(error instanceof Error ? error.message : 'Failed to save changes');
      } finally {
        isSaving.value = false;
      }
    }

    async function modifySavedView(
      title: string,
      description: string,
      visibility: UIConfigVisibility
    ): Promise<string> {
      if (!editingView.value?.id) {
        throw new Error('No view id to modify');
      }

      const res = await uiCustomizationService.modifySavedView(editingView.value.id, {
        ...editingView.value,
        title,
        description,
        visibility,
      });

      if (!res.success) {
        throw new Error('Failed to save changes');
      }

      snackbarManager.confirm('View succesfully updated');
      return editingView.value.id;
    }

    async function createSavedView(
      title: string,
      description: string,
      visibility: UIConfigVisibility
    ): Promise<string> {
      const res = await uiCustomizationService.createSavedView(name, editingIndex.value! + 1, {
        ...editingView.value,
        isOwner: true,
        title,
        description,
        visibility,
      });

      if (!res.success) {
        throw new Error('Failed to create view');
      }

      urlSavedViewId.value = res.data.configId;
      snackbarManager.confirm('View succesfully created');
      return res.data.configId;
    }

    async function cancelEdit() {
      if (!editingView.value?.id && editingIndex.value !== null) {
        // we were editing a new view, remove temporary view from array
        savedViews.value.splice(editingIndex.value, 1);
      }
      if (previousSelectedView.value) {
        await selectView(previousSelectedView.value);
      } else {
        await selectFirstViewIfPossible();
      }
      editingView.value = null;
    }

    async function selectFirstViewIfPossible() {
      await selectView(hasSavedViews.value ? savedViews.value[0] : null);
    }

    async function selectByIdIfPossible(id: string) {
      const view = savedViews.value.find((v) => v.id === id);
      if (view) {
        await selectView(view);
      }
    }

    async function reselectViewIfPossible(view: SavedView<T>) {
      const index = savedViews.value.findIndex((v) => v.id === view.id);
      if (index !== -1) {
        await selectView(savedViews.value[index]);
      }
    }

    function enforceMaxViewsLimit() {
      if (savedViews.value.length < MAX_SAVED_VIEWS) {
        return false;
      }
      dialogManager.alert({
        title: 'Maximum Views Reached',
        message: `You can only have a maximum of ${MAX_SAVED_VIEWS} saved views. Please delete an existing view before creating a new one.`,
      });
      return true;
    }

    const draggedIndex = shallowRef<number | null>(null);

    function handleDragStart(index: number) {
      draggedIndex.value = index;
    }

    async function handleDrop(targetIndex: number) {
      if (draggedIndex.value === null || draggedIndex.value === targetIndex) return;

      const originalIndex = draggedIndex.value;
      const viewToMove = savedViews.value[originalIndex];
      const currentlySelectedView = selectedView.value;

      // optimistically update the UI
      savedViews.value.splice(originalIndex, 1);
      savedViews.value.splice(targetIndex, 0, viewToMove);

      // update selectedView to track the moved selected view
      if (currentlySelectedView) {
        if (currentlySelectedView === viewToMove) {
          // the selected view was the one being moved, so it's now at targetIndex
          selectedView.value = savedViews.value[targetIndex];
        } else {
          // the selected view was not moved, but we need to find its new position
          await reselectViewIfPossible(currentlySelectedView);
        }
      }

      draggedIndex.value = null;

      if (!viewToMove.id) {
        // it's not possible to move a new view to a different position
        return;
      }

      const res = await uiCustomizationService.moveSavedView(viewToMove.id, targetIndex + 1);

      if (!res.success) {
        snackbarManager.error('Failed to move view.');
        // Revert UI change by refreshing from the server

        await refreshSavedViews();
        if (currentlySelectedView) {
          await reselectViewIfPossible(currentlySelectedView);
        }
      }
    }

    function handleDragEnd() {
      draggedIndex.value = null;
    }

    const uiSettings = useSessionStorageRef(`saved-views-ui-settings-${name}`, {
      isExpanded: false,
      showTabs: true,
    });

    const lastViewedId = useSessionStorageRef<string | null>(
      `saved-views-last-viewed-${name}`,
      null
    );

    const isSavedViewsExpanded = computed(() => uiSettings.value.isExpanded);

    function toggleSavedViewsExpanded() {
      uiSettings.value = { ...uiSettings.value, isExpanded: !uiSettings.value.isExpanded };
    }

    const showTabs = computed(() => uiSettings.value.showTabs);

    function toggleTabs() {
      uiSettings.value = { ...uiSettings.value, showTabs: !uiSettings.value.showTabs };
    }

    return {
      savedViews,
      hasSavedViews,
      editingView,
      setEditingView,
      previousSelectedView,
      setPreviousSelectedView,
      draggedIndex,
      selectedView,
      selectedIndex,
      showTabs,
      toggleTabs,
      isSavedViewsExpanded,
      toggleSavedViewsExpanded,
      isStateDirty,
      isSaving,
      isCreating,
      addNewView,
      syncView,
      deleteView,
      duplicateView,
      saveChanges,
      cancelEdit,
      handleDragStart,
      handleDrop,
      handleDragEnd,
      selectView,
      setInitialSavedViewId,
      registerRefreshData,
    };
  });
}

export type SavedViewsStore<T extends StatePersistorAny> = ReturnType<
  ReturnType<typeof createStore<T extends StatePersistor<infer S> ? S : never>>
>;
